import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, MailCheck, UserRound, Search } from "lucide-react";

/**
 * Per-deal "Referred by a listing agent" grants. A broker picks a Contact from
 * the CRM and adds them as a listing-agent grantee on this scenario. The grantee
 * logs into their own Contact portal and sees a "Referred Deals" section. Docs
 * flagged `hide_from_listing_agent` (via the doc row toggle elsewhere) are hidden.
 */
export default function ListingAgentAccessPanel({ scenarioId }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/scenarios/${scenarioId}/listing-agent-access`);
      setGrants(r.data?.grants || []);
    } catch (e) {
      toast.error("Could not load listing agent access");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [scenarioId]);

  // Search the Contacts CRM for potential listing agents to add.
  useEffect(() => {
    if (!showAdd) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get("/admin/contacts", { params: { q: q || undefined } });
        const list = Array.isArray(r.data) ? r.data : (r.data?.contacts || []);
        // Only contacts with an email can be granted access — the flow needs to
        // send them the "referred to you" heads-up email.
        const withEmail = list.filter((c) => (c.email || "").trim());
        // Sort listing-agent-tagged contacts to the top. Tag comparison is
        // case-insensitive and treats spaces / underscores / hyphens as
        // equivalent so "listing_agent", "Listing Agent", and "LISTING-AGENT"
        // all count as the same tag. Same rule applies to the `type` field.
        const norm = (s) => (s || "").toString().toLowerCase().replace(/[\s_-]+/g, "");
        const isLA = (c) =>
          norm(c.type) === "listingagent"
          || (c.tags || []).some((t) => norm(t) === "listingagent");
        withEmail.sort((a, b) => {
          const la = isLA(a) ? 0 : 1;
          const lb = isLA(b) ? 0 : 1;
          if (la !== lb) return la - lb;
          // Within the same bucket, keep alphabetical
          return (a.name || "").localeCompare(b.name || "");
        });
        setCandidates(withEmail.slice(0, 20));
      } catch (_) {
        setCandidates([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, showAdd]);

  const grant = async (contact) => {
    if (grants.some((g) => g.contact_id === contact.id)) {
      toast.info(`${contact.name} already has access to this deal`);
      setShowAdd(false);
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/admin/scenarios/${scenarioId}/listing-agent-access`, {
        contact_id: contact.id,
      });
      const info = r.data || {};
      if (info.portal_active) {
        toast.success(`${contact.name} was emailed a heads-up. They can see this deal in their portal.`);
      } else if (info.needs_invite) {
        toast.success(`${contact.name} added. Send them the standard Portal Invite from their Contact page so they can log in.`);
      } else {
        toast.success(`${contact.name} added to this deal.`);
      }
      setShowAdd(false);
      setQ("");
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not grant access";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (g) => {
    if (!window.confirm(`Remove ${g.name || g.email} from this deal? They'll lose access to the docs.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/scenarios/${scenarioId}/listing-agent-access/${g.contact_id}`);
      toast.success("Access revoked");
      await load();
    } catch (e) {
      toast.error("Could not revoke access");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="byrd-card p-6" data-testid="listing-agent-access-panel">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="font-serif text-xl font-bold">Listing Agent Access</h3>
          <p className="text-[11px] text-[#6B6558] mt-1 max-w-xl">
            Loop the referring listing agent into this deal's document portal. They'll see the
            shared docs and can upload the OM / T-12 / rent roll. Use the "Hide from listing agent"
            toggle on any doc row to keep it out of their view.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="byrd-btn byrd-btn-dark h-9 px-3 text-xs inline-flex items-center gap-1"
          data-testid="la-access-add-btn"
        >
          <Plus size={14} /> Add Listing Agent
        </button>
      </div>

      {showAdd && (
        <div className="border border-[#E4DFD1] rounded-md p-4 bg-[#FBF8F1] mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Search size={14} className="text-[#6B6558]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Contacts by name, email, or company…"
              className="flex-1 h-9 px-3 rounded-md border border-[#E4DFD1] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C89434]/40"
              autoFocus
              data-testid="la-access-search-input"
            />
            <button
              type="button"
              onClick={() => { setShowAdd(false); setQ(""); }}
              className="text-[11px] text-[#6B6558] hover:underline px-2"
            >
              Cancel
            </button>
          </div>
          {searching ? (
            <div className="text-[12px] text-[#6B6558] italic px-1">Searching…</div>
          ) : candidates.length === 0 ? (
            <div className="text-[12px] text-[#6B6558] italic px-1">
              No matching contacts with an email address. Add them to the CRM first.
            </div>
          ) : (
            <div className="divide-y divide-[#E4DFD1] max-h-72 overflow-y-auto rounded-md border border-[#E4DFD1] bg-white">
              {candidates.map((c) => {
                const already = grants.some((g) => g.contact_id === c.id);
                const isLA = c.type === "listing_agent" || (c.tags || []).includes("listing_agent");
                return (
                  <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <UserRound size={14} className="text-[#6B6558] shrink-0" />
                        <span className="text-sm font-medium truncate">{c.name || "(unnamed contact)"}</span>
                        {isLA && <span className="byrd-chip byrd-chip-gold text-[10px]">Listing Agent</span>}
                      </div>
                      <div className="text-[11px] text-[#6B6558] truncate">
                        {c.email}{c.company ? ` · ${c.company}` : ""}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={busy || already}
                      onClick={() => grant(c)}
                      className="byrd-btn byrd-btn-gold h-8 px-3 text-[11px] shrink-0 disabled:opacity-50"
                      data-testid={`la-access-grant-${c.id}`}
                    >
                      {already ? "Already added" : "Grant Access"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[#6B6558] italic">Loading…</div>
      ) : grants.length === 0 ? (
        <div className="text-sm text-[#6B6558] italic border border-dashed border-[#E4DFD1] rounded-md p-4 text-center">
          No listing agents on this deal yet. Click <b>Add Listing Agent</b> to grant access to one from your Contacts.
        </div>
      ) : (
        <div className="space-y-2">
          {grants.map((g) => (
            <div
              key={g.contact_id}
              className="flex items-center justify-between gap-3 p-3 rounded-md border border-[#E4DFD1] bg-white"
              data-testid={`la-grant-row-${g.contact_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <MailCheck size={14} className="text-[#C89434] shrink-0" />
                  <span className="text-sm font-medium truncate">{g.name || g.email}</span>
                </div>
                <div className="text-[11px] text-[#6B6558] truncate">
                  {g.email}{g.brokerage ? ` · ${g.brokerage}` : ""}
                  {g.granted_by_name ? ` · added by ${g.granted_by_name}` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => revoke(g)}
                disabled={busy}
                className="text-[#8A1F1A] hover:bg-[#8A1F1A]/5 rounded p-1.5 disabled:opacity-50"
                title="Remove access"
                data-testid={`la-grant-revoke-${g.contact_id}`}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
